/**
 * Slug-relative path helpers for the bare-root company routing scheme
 * (`/:slug/...`, SD-12120).
 *
 * Under bare-root the first URL segment is EITHER a company slug OR a real
 * top-level route, so scoping can no longer be decided structurally (there is
 * no `/c/` marker). It is decided by a reserved-word lookup: a first segment
 * that is a known route/infra word (`isReservedSlug`) is NOT a slug. That
 * reserved-slug table and global-route metadata are its only dependencies; it
 * otherwise stays free of React / service imports so the rules stay directly
 * unit-testable (see `useCompanyPath` for the hook).
 */

import { isGlobalAuthenticatedPath } from "../../router/globalAuthenticatedRoutes";
import { isReservedSlug } from "../../router/reservedSlugs";

/** First path segment of a normalized (leading-slash-stripped) path, excluding
 * any query/hash. `"settings/company?x=1"` → `"settings"`; `""` → `""`. */
const firstPathSegment = (normalized: string): string =>
  normalized.split(/[/?#]/, 1)[0] ?? "";

/**
 * Builds a slug-relative URL under the bare-root scheme.
 *
 * - With a non-reserved slug: `/<slug>/<to>` (leading slashes on `to` are
 *   normalized; an empty `to` yields the bare `/<slug>` company root).
 * - An already company-scoped target passes through unchanged, preventing
 *   stored or server-provided deep links from being prefixed twice. Under
 *   bare-root "scoped" means the first segment is a slug — i.e. a non-reserved
 *   word.
 * - Global authenticated destinations pass through unchanged because they do
 *   not depend on the active company.
 * - Without a slug (the resolver hasn't produced one yet, or a company has no
 *   slug on an older response): returns `to` as a plain absolute path — the safe
 *   passthrough that keeps legacy unprefixed navigation working.
 */
export const buildCompanyPath = (
  slug: string | undefined | null,
  to: string,
): string => {
  const normalized = to.replace(/^\/+/, "");
  const firstSegment = firstPathSegment(normalized);

  // Global authenticated destinations never carry a company scope.
  if (isGlobalAuthenticatedPath(normalized)) {
    return `/${normalized}`;
  }
  // Already company-scoped → pass through. Scoped = first segment is a slug: a
  // non-reserved word under bare-root routing.
  const alreadyScoped = firstSegment !== "" && !isReservedSlug(firstSegment);
  if (alreadyScoped) {
    return `/${normalized}`;
  }
  if (!slug) {
    return `/${normalized}`;
  }
  return normalized ? `/${slug}/${normalized}` : `/${slug}`;
};

/** Splits `/<first>/<rest>` capturing the first segment (1) and the remainder
 * including its leading slash (2); no match for `/` or the empty path. */
const FIRST_SEGMENT_PATTERN = /^\/([^/]+)(\/.*)?$/;

export type StrippedCompanyPath = {
  /** The slug segment, or `undefined` when the path is not company-scoped
   * (its first segment is a real route/infra word, or there is no segment). */
  slug?: string;
  /**
   * The path with the leading `/<slug>` removed, always leading-slashed. The
   * bare company root (`/<slug>`) strips to `/`. When the path is not
   * company-scoped the original `pathname` is returned unchanged. This is the
   * underlying path to feed `findMatchingRoute` (routeConfig paths are
   * unprefixed).
   */
  pathname: string;
};

/**
 * Splits a bare-root `/<slug>/...` pathname into its slug and the underlying
 * app-relative path — the inverse of `buildCompanyPath`. A first segment that is
 * a reserved route/infra word is NOT a slug, so it passes through with
 * `slug: undefined`. Kept free of router `params` so the loaders can strip a
 * raw request URL and unit tests can exercise the rules directly.
 */
export const stripCompanyPrefix = (pathname: string): StrippedCompanyPath => {
  const match = FIRST_SEGMENT_PATTERN.exec(pathname);
  if (!match) {
    return { pathname }; // "/" or "" — no segment to interpret.
  }
  const [, first, rest] = match;

  // Bare-root: a reserved first segment is a real route, not a slug.
  if (isReservedSlug(first)) {
    return { pathname };
  }
  return { slug: first, pathname: rest ?? "/" };
};

/**
 * Strip the deployment base path (Vite `BASE_URL`) from a pathname so it can be
 * matched against the application-relative routeConfig. On a subpath deploy
 * (`/pr-N/`) a loader-origin path — e.g. a stored `redirectTo` built from a
 * loader's request URL — can carry the base ahead of the company slug.
 * Production and unit tests use `/`, where this is a no-op.
 */
export const stripBasename = (pathname: string): string => {
  const base = import.meta.env.BASE_URL;
  if (!base || base === "/") return pathname;
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base; // "/pr-N"
  if (pathname === trimmed) return "/";
  return pathname.startsWith(`${trimmed}/`)
    ? pathname.slice(trimmed.length)
    : pathname;
};

/**
 * The active company slug for slug-relative path building, preferring in order:
 * the PENDING navigation's slug, the committed URL param, then the session
 * company's slug.
 *
 * During an in-flight navigation react-router has not yet committed the URL, so
 * the param still holds the OLD tenant. Honoring the pending destination's slug
 * keeps links built mid-switch pointed at the NEW company instead of reverting it
 * (a tab click built from the stale param would supersede the pending switch —
 * SD-12122 sibling race). `pendingPathname` is `undefined` when no navigation is
 * in flight (or the caller isn't under a data router); an unscoped pending path
 * yields no slug and falls through to the committed sources.
 */
export const resolveActiveCompanySlug = (
  pendingPathname: string | undefined,
  slugParam: string | undefined,
  sessionSlug: string | undefined | null,
): string | undefined => {
  const pendingSlug = pendingPathname
    ? stripCompanyPrefix(stripBasename(pendingPathname)).slug
    : undefined;
  return pendingSlug ?? slugParam ?? sessionSlug ?? undefined;
};

/** The subset of `Location` needed to rebuild the current page under a new tenant. */
type CompanySwitchLocation = {
  pathname: string;
  search: string;
  hash: string;
};

const searchForCompanySwitch = (pathname: string, search: string): string => {
  const phone = pathname === "/phone" || pathname.startsWith("/phone/");
  if (pathname !== "/conversations" && !phone) return search;
  const params = new URLSearchParams(search);
  if (pathname === "/conversations") params.delete("id");
  if (phone) {
    for (const key of [
      "portOrderId",
      "portingStep",
      "transferId",
      "brandId",
      "campaignId",
      "phoneNumber",
      "assignPortOrderId",
    ])
      params.delete(key);
  }
  const next = params.toString();
  return next ? `?${next}` : "";
};

/**
 * A path segment that is a tenant-owned resource identifier: a Mongo ObjectId
 * (agents, customers, territories, campaigns, …), a purely numeric provider id
 * (ServiceTitan jobs/technicians), or a UUID. Route words are never shaped like
 * any of these, so a match means the segment names a record of the company
 * being switched away from.
 */
const RESOURCE_ID_SEGMENT_PATTERN =
  /^(?:[0-9a-f]{24}|[0-9]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * The app-relative path a company switch should keep. A detail path carrying a
 * resource id (`/dispatch/customers/<id>`, `/agents/<id>/call-logic`, …) is cut
 * back to its nearest list ancestor (`/dispatch/customers`, `/agents`) — the id
 * names a record of the *previous* tenant, so carrying it lands the new tenant
 * on an invalid or forbidden page. Paths without an id segment pass through
 * unchanged.
 */
const pathnameForCompanySwitch = (pathname: string): string => {
  // E.164 numbers are tenant-owned identifiers too, outside the generic ID pattern.
  if (/^\/phone\/numbers\/[^/]+\/flow\/?$/.test(pathname))
    return "/phone/numbers";
  const segments = pathname.split("/").filter(Boolean);
  const idIndex = segments.findIndex((segment) =>
    RESOURCE_ID_SEGMENT_PATTERN.test(segment),
  );
  if (idIndex === -1) {
    return pathname;
  }
  const kept = segments.slice(0, idIndex);
  return kept.length > 0 ? `/${kept.join("/")}` : "/";
};

/**
 * Keep the current page (path + search + hash) while moving tenant identity to
 * the selected slug — except tenant-owned resource detail pages, which are cut
 * back to their list ancestor (see `pathnameForCompanySwitch`), and the
 * Conversations `id` param, which is dropped the same way.
 * With no slug it degrades to the unprefixed legacy path,
 * where localStorage remains the tenant source of truth. Used by every surface
 * that switches the active company in place (top-nav switcher, onboarding
 * pipeline, account companies, multi-company drill-in) — on slug routes the URL
 * owns tenant identity, so a session-only switch would be re-committed away by
 * `CompanySlugTenantCommitter`.
 */
export const buildCompanySwitchPath = (
  slug: string | null | undefined,
  location: CompanySwitchLocation,
): string => {
  const stripped = stripCompanyPrefix(location.pathname).pathname;
  const pathname = pathnameForCompanySwitch(stripped);
  if (pathname !== stripped) {
    // The path was cut back to a list ancestor; the query/hash belonged to the
    // dropped detail page, so they are dropped with it.
    return buildCompanyPath(slug, pathname);
  }
  const search = searchForCompanySwitch(pathname, location.search);
  return buildCompanyPath(slug, `${pathname}${search}${location.hash}`);
};

/**
 * Whether a top-nav company switch must commit `session.companyId` itself
 * rather than letting the destination URL drive it.
 *
 * - Company-scoped destination (a bare-root `/:slug` route, i.e. the stripped
 *   path carries a slug): return `false`. The switch navigates to the new slug
 *   and `CompanySlugTenantCommitter` commits the session once the loader
 *   resolves. Committing eagerly would flip `companyId` *before* that navigation
 *   lands, and on the Conversations tab that fires the inbox's `companyId`-change
 *   `setSearchParams` — a relative `replace` that pins the URL back to the old
 *   slug, so the committer then reverts the switch (SD-12122). Letting the URL
 *   win avoids the race.
 * - Unscoped destination: return `true`. There is no slug-loader committer on
 *   that mount, so the session stays the tenant source of truth. The switch
 *   carries the company in `CompanySwitchState` and
 *   `CompanySwitchTenantCommitter` commits it once the navigation lands, so a
 *   switch that is blocked and declined never moves the session (SD-17916).
 *   This includes slugless legacy targets and global authenticated routes like
 *   `/super` and `/super/accounts`, which intentionally stay unprefixed even
 *   when the selected company has a slug.
 */
export const shouldCommitSwitchInPlace = (destinationPath: string): boolean =>
  stripCompanyPrefix(destinationPath.split(/[?#]/, 1)[0] || "/").slug ===
  undefined;

/** Location state that carries an unscoped switch's company to where it lands. */
export type CompanySwitchState = { switchedCompanyId: string };

/**
 * The routeConfig path a company-slug URL should be **authorized/validated** as:
 * strip the deployment base (so a subpath-deploy `redirectTo` matches), then the
 * leading `/<slug>`, leaving the application-relative path (`/acme/settings` →
 * `/settings`; a reserved/unscoped path passes through). The bare company root
 * (`/<slug>`) strips to `/` — it renders no page of its own; the router's index
 * (`companyRootRedirect`) redirects it to the role default under the slug, so
 * there's nothing to gate here.
 *
 * Shared by `userIsAuthorized` (shell RBAC) and `getPostLoginPath` (restricted-
 * role deep-link validation) so the two can't disagree about a slug URL.
 */
export const authPathForCompanyUrl = (rawPathname: string): string =>
  stripCompanyPrefix(stripBasename(rawPathname)).pathname;
